// src/upload/multipart.ts
// THE RESUMABLE MULTIPART UPLOAD, CLIENT SIDE. initiate → presign → PUT → complete, and resume.
//
// ══════════════════════════════════════════════════════════════════════════════════════════════
// WHAT "DURABLY ACKNOWLEDGED" MEANS HERE, AND WHY EVERY NUMBER ON SCREEN COMES FROM THE SERVER
// ══════════════════════════════════════════════════════════════════════════════════════════════
// The obvious progress bar counts bytes this client has PUSHED. It is a lie in the only case that
// matters: the parts that were in flight when the signal died were pushed and never landed. A phone
// that freed local storage against that number has destroyed a capture it cannot re-record.
//
// So progress here is `acknowledged_parts` — the set R2 itself reports, re-read from the server on
// every rejoin. `safe_to_delete_local_bytes` from `GET …/uploads/:uuid` is the ONLY number this app
// will ever offer as "you may delete this much locally", and it is passed through untouched. The
// backend was explicit that this is the one number a device may free storage against; this client
// does not compute a second opinion.
//
// ══════════════════════════════════════════════════════════════════════════════════════════════
// RESUME IS NOT A SPECIAL CASE — IT IS THE ONLY CASE
// ══════════════════════════════════════════════════════════════════════════════════════════════
// There is no "start" path and separate "resume" path. `run()` always begins with initiate, which
// is replay-safe by construction and answers with `missing_part_numbers`. A first attempt gets
// [1..n]; an attempt after a kill gets [3]; an attempt for a clip already in storage gets
// `already_complete` and sends zero bytes. One code path, so the resume path cannot rot from disuse
// — it is exercised every single time.
//
// ══════════════════════════════════════════════════════════════════════════════════════════════
// THE PRESIGNED PUT CARRIES NO BEARER TOKEN
// ══════════════════════════════════════════════════════════════════════════════════════════════
// `putPart` uses the raw `fetch`, NOT api/client.ts. Two reasons, both load-bearing:
//   1. The URL is chosen by the server and points at R2, a DIFFERENT ORIGIN. Attaching the Supabase
//      JWT would hand a bearer token for the whole API to a storage host. api/client.ts sends the
//      token to `apiBase()` and nowhere else, by design; this is the call that would have violated
//      that, so it does not go through it.
//   2. SigV4 signs the request. An extra `Authorization` header is not merely unnecessary, it
//      COLLIDES with the signature's own auth mechanism and R2 rejects it.

import {
  abortUpload as apiAbort,
  completeUpload,
  initiateUpload,
  presignParts,
  uploadStatus,
  type CameraRole,
} from '../api/practice';
import { isRefusal } from '../api/client';
import type { Refusal } from '../api/refusal';
import { unreachable } from '../api/refusal';
import { STORE, idbGet, idbPut } from '../store/idb';

/** Mirrors the backend's SUPPORTED_CONTENT_TYPES (upload-service.ts CONTENT_TYPE_EXT). */
export const SUPPORTED_CONTENT_TYPES = [
  'video/mp4',
  'video/quicktime',
  'video/x-matroska',
  'video/webm',
  'video/3gpp',
  'video/mpeg',
  'application/octet-stream',
] as const;

/**
 * What a browser reports for a file the NATIVE camera produced, mapped to something the server
 * accepts. iOS hands back `video/quicktime` for a .mov; some Androids hand back an empty string.
 *
 * An empty type becomes `application/octet-stream` — which the backend explicitly accepts — rather
 * than being guessed as mp4. Guessing the container is how a file gets stored with an extension that
 * lies about its contents.
 */
export function contentTypeFor(file: { type?: string; name?: string }): string {
  const t = (file.type ?? '').toLowerCase().split(';')[0]?.trim() ?? '';
  if ((SUPPORTED_CONTENT_TYPES as readonly string[]).includes(t)) return t;
  return 'application/octet-stream';
}

export interface UploadProgress {
  phase: 'initiating' | 'presigning' | 'sending' | 'completing' | 'done' | 'held';
  /** Parts R2 has DURABLY ACKNOWLEDGED. Never a count of parts this client merely sent. */
  partsAcknowledged: number;
  partsTotal: number;
  /** Server-reported. Absent until the server has told us. */
  bytesAcknowledged: number;
  bytesTotal: number;
  /** The server's own sentence, when it has sent one. Rendered verbatim; never paraphrased. */
  message: string | null;
  /** The server's `safe_to_delete_local_bytes`, passed through. Null until the server says. */
  safeToDeleteLocalBytes: number | null;
}

export interface UploadRequest {
  athleteId: string;
  clientUploadUuid: string;
  file: Blob & { name?: string };
  contentType: string;
  /** Written onto the clip row. Without a session_id the server writes media but NO clip, and says so. */
  sessionId?: string;
  cameraRole?: CameraRole;
  recordedAt?: string;
  onProgress?: (p: UploadProgress) => void;
  signal?: AbortSignal;
}

export interface UploadSuccess {
  ok: true;
  objectUrl: string;
  mediaId: string;
  /** TRUE when the server recognised this as a replay and wrote NOTHING a second time. */
  replayed: boolean;
  clipReason: string;
  clip: Record<string, unknown> | null;
  /** How the server recognised a replay, when it did: sha256 / object present / in-flight rejoin. */
  replaySource: string;
  partsSentThisAttempt: number;
}

export type UploadOutcome = UploadSuccess | Refusal;

/** Per-upload durable progress, so a reload does not re-PUT parts R2 already has. */
interface UploadRecord {
  client_upload_uuid: string;
  athlete_id: string;
  key: string | null;
  upload_id: string | null;
  part_size: number | null;
  /** ETags for parts THIS client PUT. The server's acknowledged set is still the authority. */
  etags: Record<number, string>;
  updated_at: number;
}

async function loadRecord(uuid: string, athleteId: string): Promise<UploadRecord> {
  const existing = await idbGet<UploadRecord>(STORE.uploads, uuid);
  return existing ?? {
    client_upload_uuid: uuid,
    athlete_id: athleteId,
    key: null,
    upload_id: null,
    part_size: null,
    etags: {},
    updated_at: Date.now(),
  };
}

const saveRecord = (r: UploadRecord): Promise<void> => idbPut(STORE.uploads, { ...r, updated_at: Date.now() });

/** Injectable so the tests can drive a fake R2 without a network. */
export type PutFn = (url: string, body: Blob, signal?: AbortSignal) => Promise<{ ok: boolean; status: number; etag: string | null }>;

let putImpl: PutFn | null = null;
export function setPartPut(f: PutFn | null): void { putImpl = f; }

/**
 * PUT one part to its presigned URL, and read back the ETag.
 *
 * THE ETag IS THE POINT. `CompleteMultipartUpload` needs `{part_number, etag}` for every part, and
 * the ETag only exists in R2's response header. A client that ignores it cannot complete the upload
 * at all — it has the bytes in storage and no way to bind them together.
 *
 * `access-control-expose-headers` must include `ETag` on the bucket's CORS policy or the browser
 * hides the header from script even on a 200. That is a bucket configuration item, and it is called
 * out in the README as something the Founder must set when R2 is turned on, because the symptom
 * (every part uploads, complete always fails) looks nothing like its cause.
 */
const defaultPut: PutFn = async (url, body, signal) => {
  const res = await fetch(url, { method: 'PUT', body, ...(signal ? { signal } : {}) });
  const raw = res.headers.get('etag') ?? res.headers.get('ETag');
  return { ok: res.ok, status: res.status, etag: raw ? raw.replace(/^"|"$/g, '') : null };
};

function sliceOf(file: Blob, partNumber: number, partSize: number): Blob {
  const start = (partNumber - 1) * partSize;
  return file.slice(start, Math.min(start + partSize, file.size));
}

/**
 * Run (or resume) one upload to completion.
 *
 * Returns a `Refusal` rather than throwing on ANY server no — including the DARK case, where an
 * unconfigured deployment answers the very first call with 503 `r2_not_configured`. That refusal is
 * returned as-is and rendered by the caller as what it is. There is no branch here that turns a
 * missing bucket into a spinner, and none that retries it in a loop.
 */
export async function runUpload(req: UploadRequest): Promise<UploadOutcome> {
  const { athleteId, clientUploadUuid, file, contentType, onProgress } = req;
  const record = await loadRecord(clientUploadUuid, athleteId);
  let sent = 0;

  const emit = (p: Partial<UploadProgress> & Pick<UploadProgress, 'phase'>): void => {
    onProgress?.({
      partsAcknowledged: 0,
      partsTotal: 0,
      bytesAcknowledged: 0,
      bytesTotal: file.size,
      message: null,
      safeToDeleteLocalBytes: null,
      ...p,
    });
  };

  // ── 1 · INITIATE, which is also REJOIN. Replay-safe; never forks.
  emit({ phase: 'initiating' });
  const init = await initiateUpload(athleteId, {
    client_upload_uuid: clientUploadUuid,
    total_bytes: file.size,
    content_type: contentType,
  });
  if (isRefusal(init)) return init;

  const s = init.value;
  record.key = s.key;
  record.upload_id = s.upload_id;
  record.part_size = s.part_size;
  // Trust the SERVER's acknowledged set over our own local etag cache — R2 is the authority on what
  // landed, and a local cache can be stale (another device, a part that expired, an aborted upload).
  for (const p of s.acknowledged_parts ?? []) record.etags[p.part_number] = p.etag;
  await saveRecord(record);

  const ackCount = (): number => s.part_count - remaining.length;
  let remaining = [...(s.missing_part_numbers ?? [])];

  emit({
    phase: 'sending',
    partsAcknowledged: ackCount(),
    partsTotal: s.part_count,
    bytesAcknowledged: (s.acknowledged_parts ?? []).reduce((n, p) => n + (p.size || 0), 0),
    bytesTotal: s.total_bytes || file.size,
  });

  // ── The bytes are already in storage. Send NOTHING; bind and finish.
  // This is the sha256 / object-present recogniser doing its job, and it is worth surfacing to the
  // athlete as "already uploaded" rather than replaying a 200 MB PUT over a mobile connection.
  if (s.already_complete) {
    return finish(req, record, s.part_count, 0, s.replay_source);
  }

  // ── 2+3 · PRESIGN THE MISSING PARTS AND SEND THEM
  if (remaining.length > 0) {
    if (!s.upload_id) {
      // Not `already_complete`, yet no upload id: the server cannot presign anything. Report it as
      // a refusal rather than proceeding into a null-URL PUT — which is the exact defect shape
      // (`upload_url: null` read as success) the backend's upload lane was written to end.
      return unreachable(
        `POST /uploads (${clientUploadUuid})`,
        new Error('server returned no upload_id and did not report the upload as complete'),
      );
    }

    emit({ phase: 'presigning', partsAcknowledged: ackCount(), partsTotal: s.part_count, bytesTotal: file.size });
    const signed = await presignParts(athleteId, clientUploadUuid, {
      part_numbers: remaining,
      upload_id: s.upload_id,
      content_type: contentType,
      part_size: s.part_size,
    });
    if (isRefusal(signed)) return signed;

    const put = putImpl ?? defaultPut;
    for (const part of signed.value.parts) {
      if (req.signal?.aborted) {
        // An aborted upload is HELD, not failed and not lost. The parts already acknowledged stay
        // acknowledged; the next run rejoins and asks for what is still missing.
        emit({ phase: 'held', partsAcknowledged: ackCount(), partsTotal: s.part_count, bytesTotal: file.size,
          message: 'Paused. The chunks already uploaded are safe; this will carry on from there.' });
        return unreachable(`PUT part ${part.part_number}`, new Error('aborted by the app'));
      }
      const blob = sliceOf(file, part.part_number, signed.value.part_size || s.part_size);
      let res: Awaited<ReturnType<PutFn>>;
      try {
        res = await put(part.url, blob, req.signal);
      } catch (e) {
        const online = typeof navigator === 'undefined' ? null : navigator.onLine;
        return unreachable(`PUT part ${part.part_number}`, e, online);
      }
      if (!res.ok) {
        return {
          ok: false,
          kind: res.status === 403 ? 'forbidden' : 'server_error',
          status: res.status,
          message:
            `Storage refused chunk ${part.part_number} with HTTP ${res.status}. `
            + (res.status === 403
              ? 'The signed link had expired or was rejected. Retrying will request a fresh one.'
              : 'The chunks already uploaded are safe; this will carry on from there.'),
          reason: 'storage_put_failed',
          switchName: null,
          missing: [],
          note: null,
          body: { part_number: part.part_number, http_status: res.status },
          request: `PUT part ${part.part_number}`,
        };
      }
      if (!res.etag) {
        return {
          ok: false,
          kind: 'server_error',
          status: res.status,
          message:
            `Chunk ${part.part_number} uploaded, but storage did not return an ETag, so the upload `
            + 'cannot be finished. The bucket must expose the ETag header to the browser '
            + '(CORS ExposeHeaders must include ETag).',
          reason: 'etag_not_exposed',
          switchName: null,
          missing: [],
          note: null,
          body: { part_number: part.part_number },
          request: `PUT part ${part.part_number}`,
        };
      }
      record.etags[part.part_number] = res.etag;
      await saveRecord(record); // durable per part: a force-quit here loses at most this one part
      remaining = remaining.filter((n) => n !== part.part_number);
      sent += 1;
      emit({
        phase: 'sending',
        partsAcknowledged: ackCount(),
        partsTotal: s.part_count,
        bytesAcknowledged: Math.min(file.size, ackCount() * (s.part_size || 0)),
        bytesTotal: file.size,
      });
    }
  }

  emit({ phase: 'completing', partsAcknowledged: s.part_count, partsTotal: s.part_count, bytesTotal: file.size });
  return finish(req, record, s.part_count, sent, s.replay_source);
}

async function finish(
  req: UploadRequest,
  record: UploadRecord,
  partCount: number,
  sent: number,
  replaySource: string,
): Promise<UploadOutcome> {
  const parts = Object.entries(record.etags)
    .map(([n, etag]) => ({ part_number: Number(n), etag }))
    .sort((a, b) => a.part_number - b.part_number);

  // ── REFUSE BEFORE THE BYTES, NOT AFTER (26 Aug 2026) ──────────────────────────────────────
  // `session_id` was sent unconditionally and `camera_role` only `...(req.cameraRole ? …)`, so a
  // missing role produced a half-present pair the server rejects with 400. That refusal arrives at
  // `complete` — after the entire file is in storage. The founder's queue held a 1.9 MB clip that
  // had uploaded in full, been rejected, and left the object behind, twenty times over.
  //
  // The request was doomed the moment it was built. Checking here costs nothing and returns the
  // same refusal without spending the upload. The caller that leaves the role null is fixed too;
  // this is the guard for every future caller that forgets.
  if (req.sessionId && !req.cameraRole) {
    return {
      ok: false, status: 400, kind: 'invalid_request',
      message: 'camera_role is required alongside session_id. Nothing was uploaded — this was '
        + 'refused before sending, because the server refuses it only after the whole file has '
        + 'transferred and the object would be left behind.',
    } as UploadOutcome;
  }

  const done = await completeUpload(req.athleteId, req.clientUploadUuid, {
    parts,
    upload_id: record.upload_id,
    content_type: req.contentType,
    ...(req.sessionId ? { session_id: req.sessionId } : {}),
    ...(req.cameraRole ? { camera_role: req.cameraRole } : {}),
    total_bytes: req.file.size,
    ...(req.recordedAt ? { recorded_at: req.recordedAt } : {}),
  });
  if (isRefusal(done)) return done;

  // Ask the server what is genuinely safe to delete locally. We do NOT infer it from "complete
  // returned 200" — the whole discipline is that this number comes from storage, not from optimism.
  const st = await uploadStatus(req.athleteId, req.clientUploadUuid, {
    total_bytes: req.file.size,
    content_type: req.contentType,
  });
  req.onProgress?.({
    phase: 'done',
    partsAcknowledged: partCount,
    partsTotal: partCount,
    bytesAcknowledged: !isRefusal(st) ? st.value.bytes_acknowledged : 0,
    bytesTotal: req.file.size,
    message: !isRefusal(st) ? st.value.retry_message : null,
    safeToDeleteLocalBytes: !isRefusal(st) ? st.value.safe_to_delete_local_bytes : null,
  });

  return {
    ok: true,
    objectUrl: done.value.object_url,
    mediaId: done.value.media_id,
    replayed: done.value.replayed,
    clipReason: done.value.clip_reason,
    clip: done.value.clip,
    replaySource,
    partsSentThisAttempt: sent,
  };
}

/** Discard a capture: release the parts R2 would otherwise bill for. Idempotent server-side. */
export async function abort(athleteId: string, clientUploadUuid: string): Promise<void> {
  const record = await idbGet<UploadRecord>(STORE.uploads, clientUploadUuid);
  await apiAbort(athleteId, clientUploadUuid, { upload_id: record?.upload_id ?? null });
}
